import React, { useRef, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, CaretLeft, CaretRight, Star, Fire, Tag } from '@phosphor-icons/react';
import { getCategoryImage, useCountdown, StarRating, productsInCategory } from './shopUtils';
import { ProductCard } from './ProductCard';

export const CategoryCircles = ({ parentCats, categories, products, selectedCategory, goCategory, colors, inShop = false }) => {
  const displayCats = parentCats;
  const catScrollRef = useRef(null);
  const scrollCats = (dir) => {
    if (!catScrollRef.current) return;
    catScrollRef.current.scrollBy({ left: dir === 'left' ? -240 : 240, behavior: 'smooth' });
  };
  return (
    <div className={inShop ? 'mb-6' : 'py-12 bg-white'} data-testid="category-circles">
      <div className={inShop ? '' : 'max-w-[1200px] mx-auto px-6'}>
        {!inShop && (
          <div className="flex items-center justify-between mb-8">
            <div className="flex-1" />
            <h2 className="text-center text-[28px] md:text-[34px]" style={{ fontFamily: "'Jost', sans-serif", color: colors.dark }}>
              Nos <span style={{ color: colors.primary }}>Catégories</span>
            </h2>
            <div className="flex-1 flex justify-end gap-2">
              <button onClick={() => scrollCats('left')}
                className="w-[36px] h-[36px] rounded-full bg-slate-100 hover:bg-slate-200 flex items-center justify-center text-slate-500 transition-colors" data-testid="cat-arrow-left">
                <CaretLeft size={18} weight="bold" />
              </button>
              <button onClick={() => scrollCats('right')}
                className="w-[36px] h-[36px] rounded-full flex items-center justify-center text-white transition-colors hover:brightness-110" style={{ backgroundColor: colors.primary }} data-testid="cat-arrow-right">
                <CaretRight size={18} weight="bold" />
              </button>
            </div>
          </div>
        )}
        <div ref={catScrollRef} className={`flex ${inShop ? 'gap-5 overflow-x-auto pb-2 scrollbar-hide' : 'gap-4 md:gap-8 overflow-x-auto pb-3 scrollbar-hide'}`}>
          {displayCats.map(c => {
            const catProducts = productsInCategory(products, c, categories);
            const count = catProducts.length;
            const isActive = selectedCategory === c.id;
            const catImage = getCategoryImage(c, catProducts);
            return (
              <button key={c.id} onClick={() => goCategory(c.id)}
                className={`flex flex-col items-center gap-2.5 group flex-shrink-0 transition-all ${isActive ? 'scale-105' : ''}`}
                data-testid={`cat-circle-${c.slug}`}>
                <div className={`${inShop ? 'w-[85px] h-[85px]' : 'w-[90px] h-[90px] md:w-[140px] md:h-[140px]'} rounded-full overflow-hidden transition-all shadow-md group-hover:shadow-xl relative`}
                  style={{ border: `${isActive ? '4px' : '3px'} solid ${isActive ? colors.primary : '#e5e7eb'}` }}>
                  <div className="w-full h-full flex items-center justify-center" style={{ backgroundColor: colors.primary + '15' }}>
                    <span className="font-bold" style={{ color: colors.primary, fontSize: inShop ? '22px' : '28px' }}>{c.name[0]}</span>
                  </div>
                  {catImage && <img src={catImage} alt={c.name} className="w-full h-full object-cover absolute inset-0" loading="lazy" onError={e => { e.currentTarget.style.display = 'none'; }} />}
                </div>
                <div className="text-center">
                  <span className={`${inShop ? 'text-[13px]' : 'text-[15px]'} font-semibold block transition-colors`}
                    style={{ color: isActive ? colors.primary : '#374151' }}>{c.name}</span>
                  {!inShop && <span className="text-[13px] text-slate-400">{count} produits</span>}
                </div>
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
};

const CountdownBadge = ({ endDate, colors, large = false }) => {
  const t = useCountdown(endDate);
  if (!endDate) return null;
  const units = [{ v: t.d, l: 'Jours' }, { v: t.h, l: 'Heures' }, { v: t.m, l: 'Min' }, { v: t.s, l: 'Sec' }];
  return (
    <div className={`flex ${large ? 'gap-3' : 'gap-2'} items-center`}>
      {units.map(({ v, l }, i) => (
        <div key={i} className="flex flex-col items-center">
          <div className={`${large ? 'w-[60px] h-[60px] text-[24px]' : 'w-[44px] h-[44px] text-[18px]'} bg-white rounded-xl flex items-center justify-center font-black shadow-lg`}
            style={{ color: colors.primary }}>
            {String(v || 0).padStart(2, '0')}
          </div>
          <span className={`${large ? 'text-[11px]' : 'text-[9px]'} text-white/80 mt-1.5 font-medium uppercase tracking-wider`}>{l}</span>
        </div>
      ))}
    </div>
  );
};

export const ProductShowcase = ({ featured, homeTab, setHomeTab, colors, setView, cardProps }) => {
  const tabs = [
    { id: 'bestsellers', label: 'Best Sellers', icon: <Star size={18} weight="fill" />, items: featured.featured || [] },
    { id: 'promos', label: 'Offres du moment', icon: <Fire size={18} weight="fill" />, items: featured.promos || [] },
    { id: 'seasonal', label: 'Spécial Saison', icon: <Tag size={18} weight="fill" />, items: featured.seasonal || [] },
  ];
  const active = tabs.find(t => t.id === homeTab) || tabs[0];
  const endDate = (featured.promos || [])[0]?.promo_end_date;
  const desktopScrollRef = useRef(null);

  const scroll = (dir) => {
    if (!desktopScrollRef.current) return;
    desktopScrollRef.current.scrollBy({ left: dir === 'left' ? -320 : 320, behavior: 'smooth' });
  };

  if (!tabs.some(t => t.items.length > 0)) return null;

  return (
    <section className="py-12 md:py-16" data-testid="product-showcase" style={{ backgroundColor: homeTab === 'promos' ? colors.primary : homeTab === 'seasonal' ? '#1a1a1a' : '#ffffff' }}>
      <div className="max-w-[1300px] mx-auto px-4 md:px-6">
        <div className="flex flex-col md:flex-row items-center justify-between mb-8 gap-4">
          <div className="flex items-center gap-2 flex-wrap justify-center">
            {tabs.filter(t => t.items.length > 0).map(tab => {
              const isDark = homeTab === 'promos' || homeTab === 'seasonal';
              const isActive = homeTab === tab.id;
              return (
                <button key={tab.id} onClick={() => setHomeTab(tab.id)}
                  className={`flex items-center gap-2 px-5 py-2.5 rounded-full text-[14px] font-bold transition-all ${isActive
                    ? (isDark ? 'bg-white shadow-lg' : 'shadow-lg text-white')
                    : (isDark ? 'bg-white/20 text-white hover:bg-white/30' : 'bg-slate-100 text-slate-500 hover:bg-slate-200')}`}
                  style={isActive && !isDark ? { backgroundColor: colors.primary } : isActive && isDark ? { color: colors.primary } : {}}
                  data-testid={`tab-${tab.id}`}>
                  {tab.icon} {tab.label}
                </button>
              );
            })}
          </div>
          {homeTab === 'promos' && endDate && (
            <div className="flex items-center gap-3">
              <span className="text-[12px] font-medium uppercase tracking-wider text-white/80">Se termine dans</span>
              <CountdownBadge endDate={endDate} colors={colors} />
            </div>
          )}
          <div className="hidden md:flex items-center gap-2">
            {(() => { const isDark = homeTab === 'promos' || homeTab === 'seasonal'; return (<>
            <button onClick={() => scroll('left')}
              className={`w-[40px] h-[40px] rounded-full flex items-center justify-center transition-colors ${isDark ? 'bg-white/20 text-white hover:bg-white/30' : 'bg-slate-100 text-slate-500 hover:bg-slate-200'}`}
              data-testid="showcase-arrow-left">
              <CaretLeft size={20} weight="bold" />
            </button>
            <button onClick={() => scroll('right')}
              className={`w-[40px] h-[40px] rounded-full flex items-center justify-center transition-colors ${isDark ? 'bg-white/20 text-white hover:bg-white/30' : 'bg-slate-100 text-slate-500 hover:bg-slate-200'}`}
              data-testid="showcase-arrow-right">
              <CaretRight size={20} weight="bold" />
            </button>
            </>); })()}
          </div>
        </div>

        <div className="hidden md:block relative">
          <div ref={desktopScrollRef} className="flex gap-5 overflow-x-auto pb-2 scrollbar-hide scroll-smooth" data-testid="showcase-grid">
            {active.items.map(p => (
              <div key={p.id} className="flex-shrink-0" style={{ width: 'calc((100% - 80px) / 5)' }}>
                <ProductCard p={p} {...cardProps} />
              </div>
            ))}
          </div>
        </div>

        <div className="md:hidden relative">
          <div className="flex gap-4 overflow-x-auto pb-4 scrollbar-hide snap-x snap-mandatory">
            {active.items.slice(0, 10).map(p => (
              <div key={p.id} className="flex-shrink-0 w-[70vw] max-w-[260px] snap-start">
                <ProductCard p={p} {...cardProps} compact />
              </div>
            ))}
          </div>
        </div>

        {active.items.length > 5 && (
          <div className="text-center mt-6">
            {(() => { const isDark = homeTab === 'promos' || homeTab === 'seasonal'; return (
              <button onClick={() => setView('shop')}
                className={`inline-flex items-center gap-2 text-[14px] font-bold transition-all hover:gap-3 ${isDark ? 'text-white/80 hover:text-white' : 'hover:underline'}`}
                style={!isDark ? { color: colors.primary } : {}}
                data-testid="voir-tout-link">
                Voir tous les produits <ArrowRight size={16} weight="bold" />
              </button>
            ); })()}
          </div>
        )}

        {active.items.length === 0 && (
          <p className={`text-center py-8 text-[15px] ${(homeTab === 'promos' || homeTab === 'seasonal') ? 'text-white/60' : 'text-slate-400'}`}>Aucun produit dans cette catégorie</p>
        )}
      </div>
    </section>
  );
};

export const GoogleReviews = ({ reviews, colors, id }) => {
  if (reviews.length === 0) return null;
  return (
    <section className="py-12 md:py-16 bg-[#faf9f7]" data-testid="google-reviews">
      <div className="max-w-[1200px] mx-auto px-4 md:px-6">
        <div className="text-center mb-10">
          <div className="flex items-center justify-center gap-3 mb-3">
            <svg viewBox="0 0 24 24" className="w-[28px] h-[28px]">
              <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/>
              <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
              <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
              <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
            </svg>
            <h2 className="text-[28px] md:text-[34px]" style={{ fontFamily: "'Jost', sans-serif", color: colors.dark }}>
              Avis <span style={{ color: colors.primary }}>Google</span>
            </h2>
          </div>
          <div className="flex items-center justify-center gap-4 mb-2">
            <span className="text-[48px] font-black" style={{ color: colors.dark }}>4.8</span>
            <div>
              <div className="flex gap-1 mb-1">
                {[1,2,3,4,5].map(i => <Star key={i} size={22} weight="fill" className={i <= 5 ? 'text-amber-400' : 'text-slate-200'} />)}
              </div>
              <p className="text-[14px] text-slate-500">Basé sur <strong>36 avis</strong></p>
            </div>
          </div>
          <a href={id.google_maps_url || 'https://www.google.com/maps/place/Mag%C3%A8re/@43.5154,-4.9877,17z/'} target="_blank" rel="noopener noreferrer"
            className="inline-flex items-center gap-2 text-[13px] font-medium hover:underline" style={{ color: colors.primary }}>
            Voir tous les avis sur Google <ArrowRight size={14} weight="bold" />
          </a>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
          {reviews.map((r, i) => (
            <div key={i} className="bg-white rounded-xl p-5 shadow-sm border border-slate-100 hover:shadow-md transition-shadow" data-testid={`review-card-${i}`}>
              <div className="flex items-center gap-3 mb-3">
                <div className="w-[40px] h-[40px] rounded-full flex items-center justify-center text-[15px] font-bold text-white" style={{ backgroundColor: colors.primary }}>
                  {r.author[0]}
                </div>
                <div>
                  <p className="text-[14px] font-semibold text-slate-800">{r.author}</p>
                  <p className="text-[12px] text-slate-400">{r.date}</p>
                </div>
              </div>
              <StarRating rating={r.rating} />
              <p className="text-[14px] text-slate-600 mt-3 leading-relaxed line-clamp-3">{r.text}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export const BlogSection = ({ blogPosts, colors, getLink }) => {
  if (blogPosts.length === 0) return null;
  return (
    <section className="py-16 bg-white" data-testid="blog-section">
      <div className="max-w-[1200px] mx-auto px-6">
        <h2 className="text-center text-[28px] md:text-[36px] mb-10" style={{ fontFamily: "'Jost', sans-serif", color: colors.dark }}>
          Nos conseils pour sublimer{' '}
          <span className="relative inline-block">
            vos viandes
            <svg className="absolute -bottom-1 left-0 w-full" viewBox="0 0 500 20" preserveAspectRatio="none" style={{ height: 8 }}>
              <path d="M5 15C150 5 350 5 495 15" fill="none" stroke={colors.primary} strokeWidth="2.5" />
            </svg>
          </span>
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {blogPosts.slice(0, 3).map((post, i) => (
            <Link to={getLink(`/blog/${post.slug}`)} key={post.id || i} className="group overflow-hidden rounded-xl bg-white border border-slate-100 hover:shadow-xl transition-all duration-300">
              <div className="aspect-[4/3] overflow-hidden relative">
                {post.featured_image && <img src={post.featured_image} alt={post.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" loading="lazy" />}
                <span className="absolute top-3 left-3 bg-white/90 backdrop-blur-sm px-3 py-1.5 rounded-lg text-[11px]">
                  <span className="block text-[18px] font-bold leading-tight" style={{ color: colors.primary }}>{new Date(post.created_at).getDate()}</span>
                  <span className="text-slate-500">{new Date(post.created_at).toLocaleDateString('fr-FR', { month: 'short' })}</span>
                </span>
                <span className="absolute top-3 right-3 px-3 py-1.5 rounded-full text-[11px] font-bold text-white capitalize" style={{ backgroundColor: colors.primary }}>{post.category || 'Recettes'}</span>
              </div>
              <div className="p-5">
                <h3 className="text-[16px] font-bold text-slate-800 mb-2 line-clamp-2">{post.title}</h3>
                <p className="text-[14px] text-slate-500 line-clamp-2">{post.excerpt}</p>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
};

export const RecentlyViewedSection = ({ recentlyViewed, colors, getLink }) => {
  if (!recentlyViewed.length) return null;
  return (
    <section className="py-12 bg-[#faf9f7]" data-testid="recently-viewed-section">
      <div className="max-w-[1300px] mx-auto px-6">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-[22px] md:text-[28px] font-bold" style={{ fontFamily: "'Jost', sans-serif", color: colors.dark }}>
            Récemment <span style={{ color: colors.primary }}>consultés</span>
          </h2>
          <span className="text-[13px] text-slate-400">{recentlyViewed.length} produit(s)</span>
        </div>
        <div className="flex gap-4 overflow-x-auto pb-4 scrollbar-hide">
          {recentlyViewed.map(p => (
            <Link key={p.id} to={getLink(`/product/${p.slug}`)}
              className="flex-shrink-0 w-[200px] group bg-white border border-slate-100 hover:shadow-xl transition-all rounded-xl overflow-hidden"
              data-testid={`recent-product-${p.id}`}>
              <div className="aspect-square overflow-hidden bg-slate-50 relative">
                {(p.featured_image || p.images?.[0]) && (
                  <img src={p.featured_image || p.images[0]} alt={p.name}
                    className="w-full h-full object-contain p-3 group-hover:scale-110 transition-transform duration-500" loading="lazy" />
                )}
              </div>
              <div className="p-3.5">
                <h3 className="text-[13px] font-semibold text-slate-800 line-clamp-2 mb-2 min-h-[36px]">{p.name}</h3>
                <span className="text-[17px] font-black" style={{ color: colors.primary }}>
                  {(v => Number.isFinite(v) ? `${v.toFixed(2)} €` : 'Prix sur demande')(parseFloat(p.is_promo && p.sale_price ? p.sale_price : p.price))}
                </span>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
};
